import React from "react";
import { graphql, useFragment } from "react-relay";

import { faClipboardList } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faEllipsisVertical } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";

import { inCalendarMode, isTeams, isTouchScreen } from "~/lib/utils";
import UploadMenuItem from "~/recordings/uploads/UploadMenuItem";
import useIsUploadsEnabled from "~/recordings/uploads/useIsUploadsEnabled";
import type { DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import IconButton from "~/ui/button/IconButton";
import Menu from "~/ui/menu";

import AddNoteTitleMenuItem from "./AddNoteTitle";
import ApplyTemplateMenuItem from "./ApplyTemplate";
import { CarryForwardActionItems, CarryForwardTalkingPoints } from "./CarryForward";
import CompanionModeMenuItem from "./CompanionMode";
import CopyLinkMenuItem from "./CopyLink";
import DeleteMeetingMenuItem from "./DeleteMeeting";
import EditLinkedEvent from "./EditLinkedEvent";
import EditTemplateMenuItem from "./EditTemplate";
import ExportMenuItem from "./Export";
import LockNoteMenuItem from "./LockNote";
import MergeSeriesMenuItem from "./MergeSeries";
import NotAOneOnOne from "./NotAOneOnOne";
import RecapLanguageMenuItem from "./RecapLanguage";
import ResolvedComments from "./ResolvedComments";
import SaveAsTemplateMenuItem from "./SaveAsTemplate";
import SeriesSettingsMenuItem from "./SeriesSettings";
import ShareSeriesMenuItem from "./ShareSeries";
import VersionHistory from "./VersionHistory";

import type { MoreActionsMenu_MeetingHeader_note$key } from "~/__generated__/MoreActionsMenu_MeetingHeader_note.graphql";
import type { MoreActionsMenu_MeetingHeader_viewer$key } from "~/__generated__/MoreActionsMenu_MeetingHeader_viewer.graphql";

const viewerFragment = graphql`
	fragment MoreActionsMenu_MeetingHeader_viewer on Viewer {
		account {
			allowAiFeatures
		}
		user {
			isGuest
		}
		...SaveAsTemplate_viewer
		...MergeSeries_viewer
		...ExportMenuItem_viewer
		...VersionHistory_viewer
		...UploadMenuItem_viewer
		...LockNote_viewer
		...DeleteMeeting_viewer
		...ShareSeries_viewer
	}
`;

const noteFragment = graphql`
	fragment MoreActionsMenu_MeetingHeader_note on Note {
		id
		title
		isLocked
		viewerCanEdit
		hasMeetingRecordings
		noteType
		eventGuid
		stream {
			id
			type
		}

		...useCopyMeetingLink_note
		...AddNoteTitle_note
		...ApplyTemplate_note
		...EditTemplateMenuItem_note
		...SaveAsTemplate_note
		...RecapLanguage_note
		...SeriesSettings_note
		...CarryForward_MoreActionsMenu_note
		...MergeSeries_note
		...ExportMenuItem_note
		...VersionHistory_note
		...ResolvedComments_note
		...EditLinkedEvent_note
		...NotAOneOnOne_note
		...LockNote_note
		...DeleteMeeting_note
		...ShareSeries_note
	}
`;

export interface MoreActionsProps {
	selectedRecordingId?: string;
	onDeleteExecuted: (type: DeletionType) => void;
}

export default function MoreActionsMenu({
	viewer: viewerKey,
	note: noteKey,
	selectedRecordingId,
	onDeleteExecuted,
	inModal,
}: MoreActionsProps & {
	viewer: MoreActionsMenu_MeetingHeader_viewer$key;
	note: MoreActionsMenu_MeetingHeader_note$key;
	inModal: boolean;
}) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const shouldShowEditAndSaveTemplateMenuItem = !inCalendarMode && !isTouchScreen;

	const uploadsEnabled = useIsUploadsEnabled();

	if (!note.viewerCanEdit) {
		return (
			<Menu mobileHeaderTitle="More Actions">
				<Menu.Trigger asChild>
					<IconButton icon={faEllipsisVertical} variant="tertiary" title="More actions" />
				</Menu.Trigger>
				<Menu.Content align="end">
					<CopyLinkMenuItem note={note} />
				</Menu.Content>
			</Menu>
		);
	}

	return (
		<Menu mobileHeaderTitle="More Actions">
			<Menu.Trigger asChild>
				<IconButton icon={faEllipsisVertical} variant="tertiary" title="More actions" />
			</Menu.Trigger>
			<Menu.Content align="end">
				{!inModal && (
					<>
						<SeriesSettingsMenuItem seriesId={note.stream.id} note={note} />
						<Menu.Separator />
					</>
				)}
				<CopyLinkMenuItem note={note} />
				<AddNoteTitleMenuItem note={note} />
				{window.electron && window.electron.navigateCompanionMode && (
					<CompanionModeMenuItem url={window.location.href} />
				)}
				<Menu.Separator />
				{!isTeams &&
					!inModal &&
					(shouldShowEditAndSaveTemplateMenuItem ? (
						<Menu.Sub mobileHeaderTitle="Templates">
							<Menu.SubTrigger icon={faClipboardList}>Template…</Menu.SubTrigger>
							<Menu.SubContent>
								<ApplyTemplateMenuItem note={note} />
								<EditTemplateMenuItem note={note} />
								<SaveAsTemplateMenuItem viewer={viewer} note={note} />
							</Menu.SubContent>
						</Menu.Sub>
					) : (
						<ApplyTemplateMenuItem note={note} />
					))}
				{uploadsEnabled && !note.hasMeetingRecordings && (
					<UploadMenuItem viewer={viewer} analyticsLocationDescription="Meeting header" />
				)}
				{viewer.account.allowAiFeatures && !viewer.user.isGuest && selectedRecordingId && (
					<RecapLanguageMenuItem note={note} selectedRecordingId={selectedRecordingId} />
				)}
				{!inModal && (
					<>
						<Menu.Separator />
						<CarryForwardTalkingPoints note={note} />
						<CarryForwardActionItems note={note} />
					</>
				)}
				<Menu.Separator />
				{!isTeams && !inModal && note.stream.type !== "goalset" && <MergeSeriesMenuItem note={note} viewer={viewer} />}
				<ExportMenuItem note={note} viewer={viewer} />
				<ShareSeriesMenuItem note={note} viewer={viewer} />
				<Menu.Separator />
				<VersionHistory note={note} viewer={viewer} selectedRecordingId={selectedRecordingId} />
				<ResolvedComments note={note} />
				{!inModal && !viewer.user.isGuest && note.eventGuid && note.noteType === "oneonone" && (
					<>
						<EditLinkedEvent note={note} />
						<NotAOneOnOne note={note} />
					</>
				)}
				<Menu.Separator />
				<LockNoteMenuItem note={note} viewer={viewer} />
				<DeleteMeetingMenuItem
					note={note}
					viewer={viewer}
					selectedRecordingId={selectedRecordingId}
					onDeleteExecuted={onDeleteExecuted}
				/>
			</Menu.Content>
		</Menu>
	);
}
